import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { useDialogFocus } from './useDialogFocus';
import { useT } from '../i18n/LangProvider';

export interface ConfirmOptions {
  /** Optional bold heading above the message. */
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Styles the confirm button as destructive. */
  danger?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;
const ConfirmCtx = createContext<ConfirmFn | null>(null);

interface Pending extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

/**
 * Replaces the native, unstyled, blocking window.confirm() with an in-app modal
 * that matches the design system and shares the app's focus-trap behaviour.
 * `const confirm = useConfirm(); if (await confirm({ message })) { … }`
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const t = useT();
  const [pending, setPending] = useState<Pending | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const confirm = useCallback<ConfirmFn>(
    (opts) => new Promise<boolean>((resolve) => setPending({ ...opts, resolve })),
    [],
  );

  const settle = (value: boolean) =>
    setPending((p) => {
      p?.resolve(value);
      return null;
    });

  useDialogFocus(dialogRef, !!pending, () => settle(false));

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {pending && (
        <div
          className="confirm-overlay"
          onMouseDown={(e) => { if (e.target === e.currentTarget) settle(false); }}
        >
          <div
            ref={dialogRef}
            className="card confirm-card"
            role="alertdialog"
            aria-modal="true"
            aria-label={pending.title ?? pending.message}
            tabIndex={-1}
          >
            {pending.title && <h2 className="confirm-card__title">{pending.title}</h2>}
            <p className="confirm-card__msg">{pending.message}</p>
            <div className="confirm-card__actions">
              <button className="btn" onClick={() => settle(false)}>
                {pending.cancelLabel ?? t('Annulla')}
              </button>
              <button
                className="btn btn--brand"
                data-dialog-initial-focus
                style={pending.danger ? { background: 'var(--danger)', borderColor: 'var(--danger)', color: '#fff' } : undefined}
                onClick={() => settle(true)}
              >
                {pending.confirmLabel ?? t('Conferma')}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmCtx.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx;
}
